import { ethers } from "ethers";
import type { NetworkMetadata } from "lib/wallets";
import sample from "lodash/sample";
import { isDevelopment } from "./env";
import { ARBITRUM, BSC, AVALANCHE_FUJI, BESC, BSС_MAINNET, BSС_TESTNET, ETH_MAINNET } from "./static/chains";

export * from "./static/chains";

const { parseEther } = ethers;

export const ENV_ARBITRUM_RPC_URLS = import.meta.env.VITE_APP_ARBITRUM_RPC_URLS;
export const ENV_AVALANCHE_RPC_URLS = import.meta.env.VITE_APP_AVALANCHE_RPC_URLS;
export const ENV_BSC_RPC_URLS = import.meta.env.VITE_APP_BSC_RPC_URLS;

export const DEFAULT_CHAIN_ID = BESC;
export const CHAIN_ID = DEFAULT_CHAIN_ID;

export const SUPPORTED_CHAIN_IDS = [BESC];

// if (isDevelopment()) {
//   SUPPORTED_CHAIN_IDS.push(AVALANCHE_FUJI);
// }

export const IS_NETWORK_DISABLED = {
  [ARBITRUM]: true,
  [BSC]: true,
  [BESC]: false,
  // [BSС_MAINNET]: true,
};

export const CHAIN_NAMES_MAP = {
  // [BSС_MAINNET]: "BSC",
  [BSС_TESTNET]: "BSC Testnet",
  [ARBITRUM]: "Arbitrum",
  [BSC]: "BSC",
  [BESC]: "BESC Hyperchain",
  [AVALANCHE_FUJI]: "Bsc Fuji",
};

// added to maxPriorityFeePerGas
// applied to EIP-1559 transactions only
// is also applied to the execution fee calculation
export const GAS_PRICE_PREMIUM_MAP = {
  [ARBITRUM]: 0n,
  [BSC]: 3000000000n, // 3 gwei
  [BESC]: 1000000000000n, // 1000 gwei (fixed gas price on BESC Hyperchain)
};

// added to gasPrice
// applied to *non* EIP-1559 transactions only
//
// it is *not* applied to the execution fee calculation, and in theory it could cause issues
// if gas price used in the execution fee calculation is lower
// than the gas price used in the transaction (e.g. create order transaction)
// then the transaction will fail with InsufficientExecutionFee error.
// it is not an issue on Ronin though because the passed gas price does not affect the paid gas price.
// for example if current gas price is 0.1 gwei and UI passes 0.5 gwei the transaction
// Ronin will still charge 0.1 gwei per gas
//
// it doesn't make much sense to set this buffer higher than the execution fee buffer
// because if the paid gas price is higher than the gas price used in the execution fee calculation
// and the transaction will still fail with InsufficientExecutionFee
//
// this buffer could also cause issues on a blockchain that uses passed gas price
// especially if execution fee buffer and lower than gas price buffer defined bellow
export const GAS_PRICE_BUFFER_MAP = {
  [ARBITRUM]: 2000n, // 20%
};

/*
  that was a constant value in ethers v5, after ethers v6 migration we use it as a minimum for maxPriorityFeePerGas
*/
export const MAX_PRIORITY_FEE_PER_GAS_MAP: Record<number, bigint | undefined> = {
  [ARBITRUM]: 1500000000n,
  [BSC]: 1500000000n,
  [BESC]: 1500000000n,
  [AVALANCHE_FUJI]: 1500000000n,
};

// added to maxPriorityFeePerGas
// applied to EIP-1559 transactions only
// is not applied to execution fee calculation
export const MAX_FEE_PER_GAS_MAP = {
  [BSC]: 200000000000n, // 200 gwei
  [BESC]: 1000000000000n, // 1000 gwei (fixed on BESC Hyperchain)
};

export const HIGH_EXECUTION_FEES_MAP = {
  [ARBITRUM]: 5, // 5 USD
  [BSC]: 5, // 5 USD
  [BESC]: 5, // 5 USD
  [AVALANCHE_FUJI]: 5, // 5 USD
};

export const EXCESSIVE_EXECUTION_FEES_MAP = {
  [ARBITRUM]: 10, // 10 USD
  [BSC]: 10, // 10 USD
  [BESC]: 10, // 10 USD
  [AVALANCHE_FUJI]: 10, // 10 USD
};

export const NETWORK_EXECUTION_TO_CREATE_FEE_FACTOR = {
  [ARBITRUM]: 10n ** 29n * 5n,
  [BSC]: 10n ** 29n * 35n,
  [BESC]: 10n ** 29n * 35n,
  [AVALANCHE_FUJI]: 10n ** 29n * 2n,
} as const;

export const EXECUTION_FEE_CONFIG_V2: {
  [chainId: number]: {
    shouldUseMaxPriorityFeePerGas: boolean;
    defaultBufferBps?: number;
  };
} = {
  [BSC]: {
    shouldUseMaxPriorityFeePerGas: true,
    defaultBufferBps: 1000, // 10%
  },
  [AVALANCHE_FUJI]: {
    shouldUseMaxPriorityFeePerGas: true,
    defaultBufferBps: 1000, // 10%
  },
  [ARBITRUM]: {
    shouldUseMaxPriorityFeePerGas: false,
    defaultBufferBps: 3000, // 30%
  },
  [BESC]: {
    shouldUseMaxPriorityFeePerGas: true,
    defaultBufferBps: 1000, // 10%
  },
};

const constants = {
  // [BSС_MAINNET]: {
  //   nativeTokenSymbol: "BNB",
  //   defaultCollateralSymbol: "BUSD",
  //   defaultFlagOrdersEnabled: false,
  //   positionReaderPropsLength: 8,
  //   v2: false,
  // },

  [BSС_TESTNET]: {
    nativeTokenSymbol: "BNB",
    defaultCollateralSymbol: "BUSD",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 8,
    v2: false,
  },

  [ARBITRUM]: {
    nativeTokenSymbol: "ETH",
    wrappedTokenSymbol: "WETH",
    defaultCollateralSymbol: "USDC.e",
    defaultFlagOrdersEnabled: false,
    positionReaderPropsLength: 9,
    v2: false,

    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.0003"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.0003"),
    // contract requires that execution fee be strictly greater than instead of gte
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.000300001"),
  },

  [BSC]: {
    nativeTokenSymbol: "BNB",
    wrappedTokenSymbol: "WBNB",
    defaultCollateralSymbol: "USDT",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: false,

    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.001"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.001"),
    // contract requires that execution fee be strictly greater than instead of gte
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.00100001"),
  },

  [AVALANCHE_FUJI]: {
    nativeTokenSymbol: "RON",
    wrappedTokenSymbol: "WRON",
    defaultCollateralSymbol: "USDC",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: false,

    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.01"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.01"),
    // contract requires that execution fee be strictly greater than instead of gte
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.0100001"),
  },

  [BESC]: {
    nativeTokenSymbol: "BESC",
    wrappedTokenSymbol: "WBESC",
    defaultCollateralSymbol: "BUSDC",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: false,

    // BESC Hyperchain fixed gas: 1000 gwei × ~300k gas = 0.3 BESC per execution.
    // OrderBook.minExecutionFee is set to 0.3 BESC by setupBescTokens.js.
    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.3"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.3"),
    // contract requires strictly greater than minExecutionFee
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.300000001"),
  },
};

const ALCHEMY_WHITELISTED_DOMAINS = ["money.io", "moneyx.exchange"];

export const RPC_PROVIDERS = {
  [ETH_MAINNET]: ["https://rpc.ankr.com/eth"],
  // [BSС_MAINNET]: [
  //   "https://bsc-dataseed.binance.org",
  //   "https://bsc-dataseed1.defibit.io",
  //   "https://bsc-dataseed1.ninicoin.io",
  //   "https://bsc-dataseed2.defibit.io",
  //   "https://bsc-dataseed3.defibit.io",
  //   "https://bsc-dataseed4.defibit.io",
  //   "https://bsc-dataseed2.ninicoin.io",
  //   "https://bsc-dataseed3.ninicoin.io",
  //   "https://bsc-dataseed4.ninicoin.io",
  //   "https://bsc-dataseed1.binance.org",
  //   "https://bsc-dataseed2.binance.org",
  //   "https://bsc-dataseed3.binance.org",
  //   "https://bsc-dataseed4.binance.org",
  // ],
  [BSС_TESTNET]: ["https://data-seed-prebsc-1-s1.binance.org:8545/"],
  [ARBITRUM]: [
    "https://arb1.arbitrum.io/rpc",
    "https://arbitrum-one-rpc.publicnode.com",
    "https://1rpc.io/arb",
    "https://arbitrum-one.public.blastapi.io",
    // "https://arbitrum.drpc.org",
    "https://rpc.ankr.com/arbitrum",
  ],
  [BSC]: [
    "https://bsc-dataseed.binance.org",
    "https://bsc-dataseed1.defibit.io",
    "https://bsc-dataseed1.ninicoin.io",
    "https://bsc-dataseed2.defibit.io",
    "https://bsc-dataseed3.defibit.io",
    "https://bsc-dataseed4.defibit.io",
    "https://bsc-dataseed2.ninicoin.io",
    "https://bsc-dataseed3.ninicoin.io",
    "https://bsc-dataseed4.ninicoin.io",
    "https://bsc-dataseed1.binance.org",
    "https://bsc-dataseed2.binance.org",
    "https://bsc-dataseed3.binance.org",
    "https://bsc-dataseed4.binance.org",
  ],
  [AVALANCHE_FUJI]: [
    "https://bsc-fuji-c-chain.publicnode.com",
    "https://api.ron-test.network/ext/bc/C/rpc",
    // "https://ava-testnet.public.blastapi.io/v1/ron/fuji/public",
    // "https://rpc.ankr.com/bsc_fuji",
  ],
  [BESC]: [
    // api.moneyxpro.com/rpc (our nginx proxy → hyper) is PRIMARY: it lives on the
    // same domain as the app's API, so any client that can load the app can reach
    // it. hyper.beschyperchain.com direct is fast server-side but some browsers /
    // networks cannot reach it, and ~240 synthetic reads failing against it exhausts
    // the browser connection pool and takes everything down. Hyper stays as fallback.
    // rpc.beschyperchain.com is OMITTED — invalid CORS (Allow-Credentials:true +
    // Allow-Origin:*) which browsers reject, and ~10× slower.
    "https://api.moneyxpro.com/rpc",
    "https://hyper.beschyperchain.com",
  ],
};

export const FALLBACK_PROVIDERS = {
  [ARBITRUM]: ENV_ARBITRUM_RPC_URLS ? JSON.parse(ENV_ARBITRUM_RPC_URLS) : [getAlchemyArbitrumHttpUrl()],
  [BSC]: ENV_BSC_RPC_URLS ? JSON.parse(ENV_BSC_RPC_URLS) : [getAlchemyBscHttpUrl()],
  [BESC]: [
    "https://api.moneyxpro.com/rpc",
    "https://hyper.beschyperchain.com",
  ],
  [AVALANCHE_FUJI]: [
    "https://endpoints.omniatech.io/v1/ron/fuji/public",
    "https://api.ron-test.network/ext/bc/C/rpc",
    "https://ava-testnet.public.blastapi.io/ext/bc/C/rpc",
  ],
};

export const NETWORK_METADATA: { [chainId: number]: NetworkMetadata } = {
  // [BSС_MAINNET]: {
  //   chainId: "0x" + BSС_MAINNET.toString(16),
  //   chainName: "BSC",
  //   nativeCurrency: {
  //     name: "BNB",
  //     symbol: "BNB",
  //     decimals: 18,
  //   },
  //   rpcUrls: RPC_PROVIDERS[BSС_MAINNET],
  //   blockExplorerUrls: ["https://bscscan.com"],
  // },
  [BSС_TESTNET]: {
    chainId: "0x" + BSС_TESTNET.toString(16),
    chainName: "BSC Testnet",
    nativeCurrency: {
      name: "BNB",
      symbol: "BNB",
      decimals: 18,
    },
    rpcUrls: RPC_PROVIDERS[BSС_TESTNET],
    blockExplorerUrls: ["https://testnet.bscscan.com/"],
  },
  [ARBITRUM]: {
    chainId: "0x" + ARBITRUM.toString(16),
    chainName: "Ronin",
    nativeCurrency: {
      name: "ETH",
      symbol: "ETH",
      decimals: 18,
    },
    rpcUrls: RPC_PROVIDERS[ARBITRUM],
    blockExplorerUrls: [getExplorerUrl(ARBITRUM)],
  },
  [BSC]: {
    chainId: "0x" + BSC.toString(16),
    chainName: "Bsc",
    nativeCurrency: {
      name: "RON",
      symbol: "RON",
      decimals: 18,
    },
    rpcUrls: RPC_PROVIDERS[BSC],
    blockExplorerUrls: [getExplorerUrl(BSC)],
  },
  [AVALANCHE_FUJI]: {
    chainId: "0x" + AVALANCHE_FUJI.toString(16),
    chainName: "Bsc Fuji Testnet",
    nativeCurrency: {
      name: "RON",
      symbol: "RON",
      decimals: 18,
    },
    rpcUrls: RPC_PROVIDERS[AVALANCHE_FUJI],
    blockExplorerUrls: [getExplorerUrl(AVALANCHE_FUJI)],
  },
  [BESC]: {
    chainId: "0x944",
    chainName: "BESC Hyperchain",
    nativeCurrency: {
      name: "BESC",
      symbol: "BESC",
      decimals: 18,
    },
    rpcUrls: RPC_PROVIDERS[BESC],
    blockExplorerUrls: [getExplorerUrl(BESC)],
  },
};

export const getConstant = (chainId: number, key: string) => {
  if (!constants[chainId]) {
    throw new Error(`Unsupported chainId ${chainId}`);
  }

  if (!(key in constants[chainId])) {
    throw new Error(`Key ${key} does not exist for chainId ${chainId}`);
  }

  return constants[chainId][key];
};

export function getChainName(chainId: number) {
  return CHAIN_NAMES_MAP[chainId];
}

export function getFallbackRpcUrl(chainId: number): string {
  return sample(FALLBACK_PROVIDERS[chainId]);
}

function getAlchemyKey() {
  if (ALCHEMY_WHITELISTED_DOMAINS.includes(self.location.host)) {
    return "RcaXYTizJs51m-w9SnRyDrxSZhE5H9Mf";
  }
  return "EmVYwUw0N2tXOuG0SZfe5Z04rzBsCbr2";
}

export function getAlchemyArbitrumHttpUrl() {
  return `https://arb-mainnet.g.alchemy.com/v2/${getAlchemyKey()}`;
}

export function getAlchemyBscHttpUrl() {
  return `https://bsc-dataseed.binance.org`;
}

export function getAlchemyArbitrumWsUrl() {
  return `wss://arb-mainnet.g.alchemy.com/v2/${getAlchemyKey()}`;
}

export function getExplorerUrl(chainId) {
  if (chainId === 3) {
    return "https://ropsten.etherscan.io/";
  } else if (chainId === 42) {
    return "https://kovan.etherscan.io/";
  // } else if (chainId === BSС_MAINNET) {
  //   return "https://bscscan.com/";
  } else if (chainId === BSС_TESTNET) {
    return "https://testnet.bscscan.com/";
  } else if (chainId === ARBITRUM) {
    return "https://arbiscan.io/";
  } else if (chainId === BSC) {
    return "https://bscscan.com/";
  } else if (chainId === AVALANCHE_FUJI) {
    return "https://testnet.snowtrace.io/";
  } else if (chainId === BESC) {
    return "https://explorer.beschyperchain.com/";
  }
  return "https://etherscan.io/";
}

export function getTokenExplorerUrl(chainId: number, tokenAddress: string) {
  return `${getExplorerUrl(chainId)}token/${tokenAddress}`;
}

export function getHighExecutionFee(chainId) {
  return HIGH_EXECUTION_FEES_MAP[chainId] ?? 5;
}

export function getExcessiveExecutionFee(chainId) {
  return EXCESSIVE_EXECUTION_FEES_MAP[chainId] ?? 10;
}

export function isSupportedChain(chainId: number) {
  return SUPPORTED_CHAIN_IDS.includes(chainId);
}
